/**
 * Schedule clock source.
 *
 * Returns the epoch-ms "now" used by every live schedule surface (the live banner, the calendar
 * now-line, and the Agenda today-selection). Centralizing it here means a single override point.
 *
 * Testing aid: a `?__now=<ISO>` query param shifts the clock so you can preview day-of states for an
 * event whose real date hasn't arrived. The parsed target becomes the new base and the clock keeps
 * ticking forward from it, so live transitions still happen in real time. Example:
 * `?__now=2026-06-08T11:00:00-05:00`.
 *
 * Who may use it: outside production (local + preview), anyone. In production, only an authenticated
 * Nowadays admin — that status can't be known from the public build-time env var, so it's resolved
 * server-side in the RSC and threaded in via {@link armScheduleClock}. For everyone else, production
 * ignores the param exactly as before. The gate fails closed: until armed, the admin disjunct is
 * `false`.
 */

/** Cached offset (target − real) in ms. `null` until resolved, or when invalidated by a re-arm. */
let cachedOffsetMs: number | null = null;

/**
 * The `window.location.search` the cached offset was resolved from. The module lives across client
 * (soft) navigations, so we key the cache on the query string: navigating from a `?__now=`-shifted
 * page to one without the param re-resolves to 0 instead of leaking the old offset.
 */
let cachedSearch: string | null = null;

/**
 * Whether the current viewer is a server-verified, authenticated Nowadays admin, as armed on the
 * client by {@link armScheduleClock}. Defaults `false` (fail closed) so the production override stays
 * off until the server explicitly arms it `true`.
 */
let armedIsNowadaysAdmin = false;

/** True unless this is the production Vercel environment. Build-time inlined for `NEXT_PUBLIC_*`. */
const IS_PRODUCTION = process.env.NEXT_PUBLIC_VERCEL_ENV === "production";

/** True when the `?__now=` override may be applied for the current viewer (see file header). */
function overrideAllowed(): boolean {
  return !IS_PRODUCTION || armedIsNowadaysAdmin;
}

/**
 * Arm the schedule clock with the server-resolved admin flag. Call once, high in the client tree
 * (the public wrapper, above any schedule surface) so it runs before the override is first read.
 * Idempotent per value; when the flag actually changes it invalidates the cached offset so the next
 * read re-evaluates the gate.
 *
 * This is the ONLY way to enable the `?__now=` override in production — it can't be flipped on from
 * the public env var, localStorage, or the URL alone.
 *
 * @param isNowadaysAdmin - Server-resolved: is the current viewer an authenticated Nowadays admin.
 * @returns Nothing.
 */
export function armScheduleClock(isNowadaysAdmin: boolean): void {
  if (armedIsNowadaysAdmin === isNowadaysAdmin) return;
  armedIsNowadaysAdmin = isNowadaysAdmin;
  // Gate changed — drop any offset resolved under the old gate so the next read re-evaluates.
  cachedOffsetMs = null;
  cachedSearch = null;
}

/** Resolve (and cache) the clock offset from the `?__now=` param, or 0 when absent/disabled. */
function resolveOffsetMs(): number {
  // Server never shifts the clock, and a disabled gate means no offset. Neither path caches, so the
  // first client read (or an arm that flips the gate) re-resolves cleanly.
  if (typeof window === "undefined" || !overrideAllowed()) return 0;

  // Re-resolve whenever the query string changes (the module survives client-side route changes).
  const search = window.location.search;
  if (cachedOffsetMs !== null && cachedSearch === search) return cachedOffsetMs;
  cachedSearch = search;

  const param = new URLSearchParams(search).get("__now");
  if (!param) {
    cachedOffsetMs = 0;
    return cachedOffsetMs;
  }
  const target = Date.parse(param);
  if (!Number.isFinite(target)) {
    // Loud, not silent: an unparseable value would otherwise just fall back to the real clock and
    // look like the feature is broken. Most common cause: a non-ISO value like a single-digit hour.
    console.warn(
      `schedule-clock: ignoring ?__now=${param} — not a parseable ISO date-time. ` +
        `Use a zero-padded ISO string, e.g. 2026-06-08T08:30:00-05:00.`
    );
    cachedOffsetMs = 0;
    return cachedOffsetMs;
  }
  cachedOffsetMs = target - Date.now();
  return cachedOffsetMs;
}

/**
 * The current time in epoch ms for live schedule computations. Equals `Date.now()` in production
 * and locally without the param; with a valid `?__now=` (non-prod) it ticks forward from the target.
 *
 * @returns Current epoch time in ms (possibly shifted by the `?__now=` override).
 */
export function scheduleNow(): number {
  return Date.now() + resolveOffsetMs();
}
